import React, { FC, useRef } from 'react';
import { isEqual } from 'lodash';

import { Card } from '../../types';
import { DeckCard } from './deck-card';
import {
  HandCard,
  SUIT_GLYPH,
  SUIT_TITLE,
  cardKey,
  groupHandBySuit,
} from './hand-layout';
import type { CardPosition } from './cards-with-layout';
import './hand-strip.css';

/**
 * Compact hand for phones / short landscape screens: a horizontally
 * scrollable strip grouped by suit. Cards overlap only enough to keep every
 * card's rank + suit corner visible and its exposed tap area ≥ 44px wide, so
 * each card can be picked unambiguously. Legal-move state (`disabled`) and the
 * `onClickCard(card, screenRect)` contract are unchanged from the fan layout.
 */
export interface HandStripProps {
  cards: HandCard[];
  /** Cards that are currently raised / in flight; their slot stays reserved. */
  hiddenCards?: (Card | null | undefined)[];
  onClickCard: (card: Card, pos: CardPosition) => () => void;
}

export const HandStrip: FC<HandStripProps> = ({
  cards,
  hiddenCards = [],
  onClickCard,
}) => {
  const groups = groupHandBySuit(cards);
  const isHidden = (card: Card) =>
    hiddenCards.some(hidden => !!hidden && isEqual(hidden, card));
  const playable = cards.filter(
    ({ disabled, ...card }) => !disabled && !isHidden(card)
  ).length;

  return (
    <div
      className="bh-hand"
      role="group"
      aria-label={`Your hand: ${cards.length} card${
        cards.length === 1 ? '' : 's'
      }, ${playable} playable`}
      data-testid="hand-strip"
    >
      <div className="bh-hand__scroller">
        {groups.map(group => (
          <div
            key={group.suite}
            className="bh-hand__suit"
            data-suit={group.suite}
            role="group"
            aria-label={`${SUIT_TITLE[group.suite]}: ${group.cards.length}, ${
              group.playable
            } playable`}
          >
            <div className="bh-hand__label" aria-hidden="true">
              <span className="bh-hand__glyph">{SUIT_GLYPH[group.suite]}</span>
              {group.cards.length}
            </div>
            <div className="bh-hand__cards">
              {group.cards.map(({ card: { disabled, ...card } }) =>
                isHidden(card) ? (
                  <div
                    key={cardKey(card)}
                    className="bh-hand__card bh-hand__card--placeholder"
                    aria-hidden="true"
                  />
                ) : (
                  <HandStripCard
                    key={cardKey(card)}
                    card={card}
                    disabled={!!disabled}
                    onClickCard={onClickCard}
                  />
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface HandStripCardProps {
  card: Card;
  disabled: boolean;
  onClickCard: (card: Card, pos: CardPosition) => () => void;
}

const HandStripCard: FC<HandStripCardProps> = ({
  card,
  disabled,
  onClickCard,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const onClick = () => {
    if (!ref.current) return;
    onClickCard(card, ref.current.getBoundingClientRect())();
  };

  return (
    <div
      className="bh-hand__card"
      data-card={cardKey(card)}
      data-disabled={disabled || undefined}
    >
      <DeckCard
        cardRef={ref}
        card={card}
        onClick={onClick}
        disabled={disabled}
      />
    </div>
  );
};
